import {_} from 'lodash';
import * as ko from "knockout";
import {ISyncField, TBaseOrObservableType} from "../../index";
import {ObjectTypeId} from "../../constants";
import {global, lm} from "../../appContext";
import pubsub from "../../pubsub";
import {nsTooltip} from "../../util/nsTooltip";
import {IComboboxV2Options} from "./v2";
import {convertToArray, getAnyValue, getSourceTranslation} from "../../util/misc";
import {nsSyncFields} from "../../req/nsSyncFields";
import {KOSubscriptionTracker} from "../../util/KOSubscriptionTracker";
import syncfieldHtml from './syncfield.html?raw';

export const template = syncfieldHtml;

export interface IComboboxOptions_syncfield {
    comboboxOptions?: Partial<IComboboxV2Options<ISyncField>>
    objectTypeIds?: TBaseOrObservableType<ObjectTypeId | ObjectTypeId[]>
    showIcon?: boolean
}

export class viewModel {
    /** Merged options object shared with v2 (reload / isReady written onto this). */
    private readonly v2Options: IComboboxV2Options<ISyncField>
    private readonly selected: KnockoutObservable<ISyncField | null>
    private readonly objectTypeIdsSubscription?: KnockoutSubscription
    private readonly isReady: KnockoutObservable<boolean>
    private readonly showIcon: boolean
    private readonly kosub: KOSubscriptionTracker = new KOSubscriptionTracker()

    constructor(private params: IComboboxOptions_syncfield) {
        this.showIcon = params.showIcon === true
        const callerOptions = params.comboboxOptions ?? {}
        this.selected = callerOptions.selected = callerOptions.selected ?? ko.observable<ISyncField | null>(null)
        this.isReady = callerOptions.isReady = callerOptions.isReady ?? ko.observable(false)
        const defaults: IComboboxV2Options<ISyncField> = {
            inputAttrs: {placeholder: lm.t('none selected')},
            initialMatch: o => o.syncfieldid == this.selected()?.syncfieldid,
            isReady: this.isReady,
            option: {
                label: o => getSourceTranslation(o),
                tooltip: o => o ? lm.t(nsTooltip.getTitle_syncfield(o) ?? '') : '',
                sortBy: [],
            },
            promise: () => nsSyncFields.getSyncFieldsMulti(this.getObjectTypeIds()).request,
            selected: this.selected,
        };
        this.v2Options = _.defaultsDeep(callerOptions, defaults) as IComboboxV2Options<ISyncField>
        // Caller-supplied data wins over the default promise source.
        if (this.v2Options.data != null) {
            delete this.v2Options.promise
        }

        // reload is assigned when v2 mounts. Events before that are a no-op (reload?.) —
        // acceptable; the child's own initial fetch covers startup.
        const reload = () => this.v2Options.reload?.()

        this.kosub.subscribe(global.sourceTranslation, reload)
        this.kosub.subscribe(lm.selectedLanguage, reload)

        if (params.objectTypeIds && ko.isSubscribable(params.objectTypeIds)) {
            this.kosub.subscribe(params.objectTypeIds, reload)
        }
    }

    private getObjectTypeIds = ko.pureComputed((): ObjectTypeId[] => {
        const objectTypeIds = getAnyValue(this.params.objectTypeIds)
        if (!objectTypeIds) {
            return [ObjectTypeId.User, ObjectTypeId.Department, ObjectTypeId.Group, ObjectTypeId.FormData]
        }
        return convertToArray(objectTypeIds);
    })

    private getObjectTypeId = ko.pureComputed((): ObjectTypeId => {
        return this.getObjectTypeIds()[0] ?? ObjectTypeId.User
    })

    dispose() {
        pubsub.off(this)
        this.objectTypeIdsSubscription?.dispose()
        this.kosub.unsuscribeAll()
    }
}
